import React, { type JSX } from 'react';
import Table, { type TableColumn } from '../Table/Table';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import VerticalContent from '../VerticalContent/VerticalContent';
import {
  IconDefaultsContextProvider,
  iconSizeLarge,
} from '../../contexts/useIconDefaults';
import useTheme from '../../contexts/useTheme';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import WarningParagraph from '@resources/js/components/WarningParagraph/WarningParagraph';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';

export type BuildsItemSetsSectionViewModel = {
  title: string;
  description: string;
  warning: string | null;
  table: {
    columns: Record<
      'item' | 'dps' | 'popularityAndReportLink',
      BuildsItemSetsSectionViewModelColumn
    > &
      Partial<
        Record<
          'maxKey' | 'hps' | 'popularity',
          BuildsItemSetsSectionViewModelColumn
        >
      >;
    data: Array<{
      item: string;
      maxKey?: string;
      dps: string;
      hps?: string;
      popularity: string;
      popularityAndReportLink: string;
    }>;
  };
};

export type BuildsItemSetsSectionViewModelColumn = Pick<
  TableColumn,
  'header' | 'textAlign' | 'noWrap'
>;

const iconDefaults = {
  item: { size: iconSizeLarge },
};

export default function BuildsItemSetsSection({
  title,
  description,
  warning,
  table,
}: BuildsItemSetsSectionViewModel): JSX.Element {
  const theme = useTheme();

  return (
    <VerticalContent>
      <EnhancedMarkdown
        markdown={`
# ${title}

${description}
`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      {warning && (
        <WarningParagraph>
          <EnhancedMarkdown markdown={warning} wrapped={false} />
        </WarningParagraph>
      )}
      <IconDefaultsContextProvider overrides={iconDefaults}>
        {table.data.length > 0 ? (
          <Table
            {...{
              ...table,
              columns: {
                ...table.columns,
                item: {
                  ...table.columns.item,
                  maxWidth: 230,
                },
              },
            }}
            enhancedMarkdownEnabled={true}
            truncate={{
              limit: 6,
              showMoreButton: { styles: theme.primaryButtonStyles },
            }}
          />
        ) : (
          <BuildsNoDataPlaceholder />
        )}
      </IconDefaultsContextProvider>
    </VerticalContent>
  );
}
